import { useState, useEffect, useRef } from "react";

import { motion, AnimatePresence } from "framer-motion";
import Lottie from "lottie-react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import lunovaLogo from "../assets/lunova-logo.png";
import poofAnimation from "../assets/poof2.json";
import swooshSound from "../assets/swoosh.mp3";
import apiService from "../services/api";
import { debugLog } from "../utils/debugLog";
import { resolveLoginPlaylist } from "../utils/playlistSelection";
import webPlayerStorage from "../utils/webPlayerStorage";

const WebPlayerProfiles = () => {
  const navigate = useNavigate();

  const [newProfileName, setNewProfileName] = useState("");
  const [selectedAvatar, setSelectedAvatar] = useState(null);
  const [creatingProfile, setCreatingProfile] = useState(false);
  const [selectingProfile, setSelectingProfile] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [availableAvatars, setAvailableAvatars] = useState([]);
  const [loadingAvatars, setLoadingAvatars] = useState(false);
  const [showAvatarModal, setShowAvatarModal] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [selectedProfileForMenu, setSelectedProfileForMenu] = useState(null);
  const [menuFocus, setMenuFocus] = useState("edit");
  const [logoutConfirm, setLogoutConfirm] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const [deletingProfile, setDeletingProfile] = useState(null);
  const [backdrop, setBackdrop] = useState(null);
  const [movieLogo, setMovieLogo] = useState(null);
  const longPressTimer = useRef(null);
  const audioRef = useRef(null);

  // get data from location state router
  const loginAuth = webPlayerStorage.getLoginAuth();
  const playlistData = loginAuth?.playlist || {};
  const rawProfiles = loginAuth?.profiles || [];
  const mappedProfiles = rawProfiles.map((profile) => ({
    id: profile.profileId,
    name: profile.profileName,
    avatar: profile.avatarUrl || null,
  }));

  const [showProfileCreation, setShowProfileCreation] = useState(
    mappedProfiles.length === 0
  );
  const [isFirstTimeUser, setIsFirstTimeUser] = useState(
    mappedProfiles.length === 0
  );
  const [profiles, setProfiles] = useState(mappedProfiles);

  useEffect(() => {
    if (showAvatarModal) {
      fetchAvatars();
    }
  }, [showAvatarModal]);

  // Fetch trending backdrop
  useEffect(() => {
    const fetchBackdrop = async () => {
      try {
        const response = await fetch(
          "https://api.lunova.app/v2/login/trending",
          {
            headers: {
              Authorization: "Bearer NoAuth",
            },
          }
        );
        const data = await response.json();

        if (data?.data) {
          setBackdrop(data.data.backdropPath);
          setMovieLogo(data.data.logoPath);
        }
      } catch (error) {
        console.error("Failed to fetch backdrop:", error);
      }
    };

    fetchBackdrop();
  }, []);

  const fetchAvatars = async () => {
    try {
      setLoadingAvatars(true);
      debugLog("Fetching avatars from direct API");

      // Use the direct getAvatars method (not proxy)
      const response = await apiService.webPlayerGetAvatars();

      debugLog("Avatar API response:", response);

      // The API returns an array of avatar URLs directly
      const avatarData = response.data || response || [];

      // Handle both array of URLs and array of objects
      let avatars = [];

      if (Array.isArray(avatarData) && avatarData.length > 0) {
        // Check if first item is a string (URL) or object
        if (typeof avatarData[0] === "string") {
          // It's an array of URLs
          avatars = avatarData;
        } else if (avatarData[0].url || avatarData[0].image) {
          // It's an array of objects with url/image property
          avatars = avatarData.map((item) => item.url || item.image);
        } else {
          // Unknown format, use as is
          avatars = avatarData;
        }
      }

      if (avatars.length > 0) {
        setAvailableAvatars(avatars);
        debugLog("Set avatars from API:", avatars.length, "avatars");
      } else {
        debugLog("No avatars from API");
        // If no avatars from API, show empty state
        setAvailableAvatars([]);
      }
    } catch (error) {
      console.error("Error fetching avatars:", error);
      console.error("Error details:", {
        message: error.message,
        status: error.status,
        response: error.response,
      });

      // Show user-friendly error message
      if (
        error.message?.includes("401") ||
        error.message?.includes("Unauthorized")
      ) {
        console.error("Authentication issue when fetching avatars");
      } else if (error.message?.includes("404")) {
        console.error("Avatars endpoint not found");
      } else if (
        error.message?.includes("Network") ||
        error.message?.includes("fetch")
      ) {
        console.error("Network issue when fetching avatars");
      }

      // On error, no avatars available
      debugLog("Could not fetch avatars from API");
      setAvailableAvatars([]);
    } finally {
      setLoadingAvatars(false);
    }
  };

  const handleCreateProfile = async () => {
    if (!newProfileName.trim()) {
      toast.error("Please enter a profile name");

      return;
    }

    setCreatingProfile(true);
    try {
      // Use selected avatar or default to logo.png
      const avatarUrl = selectedAvatar
        ? typeof selectedAvatar === "string"
          ? selectedAvatar
          : selectedAvatar.url || selectedAvatar.image
        : lunovaLogo;

      // Prepare profile data for API
      const profileData = {
        profileName: newProfileName.trim(),
        avatarUrl,
        isKids: false, // You can add a toggle for this if needed
      };

      // Create profile via direct API (not proxy)
      const response = await apiService.webPlayerCreateProfile(profileData);

      // The API should return the created profile
      const createdProfile = response.data || response;

      const newProfile = {
        id: createdProfile.profileId || createdProfile.id || Date.now(),
        name:
          createdProfile.profileName ||
          createdProfile.name ||
          newProfileName.trim(),
        avatar:
          createdProfile.avatarUrl ||
          createdProfile.avatar ||
          (typeof selectedAvatar === "string"
            ? selectedAvatar
            : selectedAvatar?.url || selectedAvatar?.image),
        isKids: createdProfile.isKids || false,
      };

      setProfiles([...profiles, newProfile]);
      setShowProfileCreation(false);
      setIsFirstTimeUser(false);
      setNewProfileName("");
      setSelectedAvatar(null);
      toast.success("Profile created successfully!");

      // Update loginAuth in storage with the new profile
      const loginAuth = webPlayerStorage.getLoginAuth();

      if (loginAuth) {
        loginAuth.profiles = loginAuth.profiles || [];
        loginAuth.profiles.push({
          profileId: newProfile.id,
          profileName: newProfile.name,
          avatarUrl: newProfile.avatar,
          isKids: newProfile.isKids || false,
        });
        webPlayerStorage.setLoginAuth(loginAuth);
      }

      // Navigate to profiles page to show the new profile
      // Don't auto-select, let user choose from the profiles page
      if (isFirstTimeUser) {
        // For first-time users, refresh to show profiles page
        window.location.reload();
      }
    } catch (error) {
      console.error("Error creating profile:", error);
      toast.error(error.message || "Failed to create profile");
    } finally {
      setCreatingProfile(false);
    }
  };

  const handleSelectProfile = async (profile) => {
    setSelectingProfile(profile.id);
    try {
      // Prepare data for API
      const profileData = {
        profileId: profile.id,
        profileName: profile.name,
      };

      // Call the direct profile login API to get the real webPlayerToken
      const response = await apiService.webPlayerLoginProfile(profileData);

      // Store the real webPlayerToken
      if (response.token) {
        webPlayerStorage.setToken(response.token);
      }

      // Store profile info for the player using webPlayerStorage
      localStorage.setItem("selectedProfile", JSON.stringify(profile));
      webPlayerStorage.setProfile(profile);

      // Keep the playlist selected during login when it belongs to this account.
      const profilePlaylists = response.playlists || [];
      const activePlaylist = resolveLoginPlaylist(
        profilePlaylists,
        playlistData
      );

      if (activePlaylist) {
        webPlayerStorage.setPlaylist(activePlaylist);
      }

      // Navigate to web player home with the profile and token
      navigate(`/web-player/home`, {
        state: {
          profile,
          playlist: activePlaylist,
          token: response.token,
        },
      });
    } catch (error) {
      console.error("Error selecting profile:", error);
      toast.error(error.message || "Failed to select profile");
    } finally {
      setSelectingProfile(null);
    }
  };

  const handleDeleteProfile = async (profileId) => {
    if (profiles.length === 1) {
      toast.error("You must have at least one profile");
      setShowProfileMenu(false);

      return;
    }

    try {
      // Play swoosh sound if available
      if (audioRef.current) {
        audioRef.current.play().catch((e) => debugLog("Audio play failed:", e));
      }

      // Set deleting animation
      setDeletingProfile(profileId);
      setShowProfileMenu(false);

      // Wait for animation
      await new Promise((resolve) => setTimeout(resolve, 800));

      // Delete profile via direct API (not proxy)
      await apiService.webPlayerDeleteProfile(profileId);

      setProfiles(profiles.filter((p) => p.id !== profileId));
      toast.success("Profile deleted");
    } catch (error) {
      console.error("Error deleting profile:", error);
      toast.error(error.message || "Failed to delete profile");
    } finally {
      setDeletingProfile(null);
    }
  };

  const handleLongPressStart = (profile) => {
    longPressTimer.current = setTimeout(() => {
      setSelectedProfileForMenu(profile);
      setMenuFocus("edit");
      setLogoutConfirm(false);
      setShowProfileMenu(true);
      // Vibrate if supported
      if (navigator.vibrate) {
        navigator.vibrate(50);
      }
    }, 500); // 500ms hold time
  };

  const handleLogout = () => {
    webPlayerStorage.clearAll();
    navigate("/web-player/login");
  };

  const openProfileMenu = (profile) => {
    setSelectedProfileForMenu(profile);
    setMenuFocus("edit");
    setLogoutConfirm(false);
    setShowProfileMenu(true);
  };

  const handleLongPressEnd = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  return (
    <>
      <motion.div
        animate={{ opacity: 1 }}
        className="profile-screen relative flex min-h-screen flex-col items-center justify-center overflow-hidden p-8"
        initial={{ opacity: 0 }}
      >
        <style>{`
          .profile-screen { --u: min(2px, calc(100vw / 960)); }
          .profile-title { font-size: calc(24 * var(--u)); font-weight: 700; }
          .profile-subtitle {
            margin-top: calc(8 * var(--u));
            font-size: calc(16 * var(--u));
            color: #b0b0b0;
          }
          .profile-row {
            margin-top: calc(48 * var(--u));
            display: flex;
            align-items: flex-start;
            justify-content: center;
            gap: 0;
          }
          .profile-item {
            position: relative;
            flex: 0 0 auto;
            margin: 0 calc(-8 * var(--u));
            padding: calc(6 * var(--u));
            transform-origin: center center;
            transition: transform 180ms ease, opacity 180ms ease;
          }
          .profile-avatar {
            width: min(calc(150 * var(--u)), 220px);
            height: min(calc(150 * var(--u)), 220px);
          }
          .profile-face { box-shadow: 0 0 0 0 transparent; }
          .profile-item[data-focused="true"] .profile-face {
            box-shadow: 0 0 0 2px #fff;
          }
          .profile-name {
            margin-top: calc(16 * var(--u));
            font-size: calc(18 * var(--u));
            font-weight: 300;
          }
          .profile-item[data-focused="true"] .profile-name { font-weight: 500; }
          .profile-add {
            margin-top: calc(40 * var(--u));
            gap: calc(10 * var(--u));
            border-radius: 999px;
            border: 1px solid rgba(255, 255, 255, 0.4);
            background: transparent;
            padding: calc(14 * var(--u)) calc(32 * var(--u));
            font-size: calc(16 * var(--u));
            font-weight: 500;
            color: #fff;
          }
          .profile-add:hover { background: #fff; color: #0a0f1e; }
          .profile-trending {
            position: absolute;
            left: calc(48 * var(--u));
            bottom: calc(32 * var(--u));
            width: 18%;
            max-height: calc(80 * var(--u));
          }
        `}</style>
        <div
          className="absolute inset-0 z-0"
          style={{
            background:
              "linear-gradient(to top right, #030305 0%, #0E0E18 50%, #1C1C2A 100%)",
          }}
        >
          {backdrop ? (
            <motion.img
              alt=""
              animate={{ opacity: 0.2 }}
              className="h-full w-full object-cover"
              initial={{ opacity: 0 }}
              src={backdrop}
              transition={{ duration: 1 }}
            />
          ) : null}
          {backdrop ? (
            <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-black/20 to-transparent" />
          ) : null}
        </div>

        {movieLogo && (
          <img
            alt=""
            className="profile-trending z-10 object-contain object-left"
            src={movieLogo}
          />
        )}

        {/* Close button to go back to dashboard */}
        <button
          aria-label="Back to Dashboard"
          className="absolute bottom-8 right-8 z-10 text-white text-sm font-medium bg-black/50 px-4 py-2 rounded-lg opacity-40 hover:opacity-100 hover:scale-105 transition-all"
          onClick={() => {
            // Clear all session data
            webPlayerStorage.clearAll();
            // Redirect to dashboard
            window.location.href = "https://dashboard.lunova.app";
          }}
        >
          Dashboard
        </button>

        <div className="z-10 flex flex-col items-center">
          <h1 className="profile-title text-white">Who&apos;s watching?</h1>
          <p className="profile-subtitle">Long press to manage your profiles</p>
        </div>

        <div className="z-10 flex flex-col items-center">
          <div className="profile-row">
            <AnimatePresence mode="popLayout">
              {profiles.map((profile, index) => {
                const distance = Math.abs(index - focusedIndex);
                const focused = distance === 0;

                return (
                  <div
                    className="profile-item flex flex-col items-center"
                    data-focused={focused ? "true" : "false"}
                    key={profile.id}
                    style={{
                      opacity: focused ? 1 : Math.max(0.3, 1 - 0.25 * distance),
                      transform: `scale(${Math.max(0.46, 1 - 0.18 * distance)})`,
                      zIndex: 10 - distance,
                    }}
                  >
                    <button
                      className="flex flex-col items-center"
                      disabled={
                        selectingProfile === profile.id ||
                        deletingProfile === profile.id
                      }
                      type="button"
                      onClick={() => {
                        if (showProfileMenu) return;
                        setFocusedIndex(index);
                        handleSelectProfile(profile);
                      }}
                      onContextMenu={(event) => {
                        event.preventDefault();
                        setFocusedIndex(index);
                        openProfileMenu(profile);
                      }}
                      onMouseDown={() => handleLongPressStart(profile)}
                      onMouseEnter={() => setFocusedIndex(index)}
                      onMouseLeave={handleLongPressEnd}
                      onMouseUp={handleLongPressEnd}
                      onTouchEnd={handleLongPressEnd}
                      onTouchStart={() => handleLongPressStart(profile)}
                    >
                      <div className="profile-avatar relative flex items-center justify-center p-1">
                        {deletingProfile === profile.id ? (
                          <div className="absolute inset-0 z-20 flex items-center justify-center">
                            <Lottie
                              animationData={poofAnimation}
                              autoplay={true}
                              loop={false}
                              style={{ width: 180, height: 180 }}
                            />
                          </div>
                        ) : (
                          <div className="profile-face absolute inset-1 flex items-center justify-center overflow-hidden rounded-full bg-gray-700 text-2xl font-bold text-white">
                            {profile.avatar ? (
                              <img
                                alt={profile.name}
                                className="h-full w-full object-cover"
                                src={profile.avatar}
                                onError={(e) => {
                                  e.target.style.display = "none";
                                  e.target.parentElement.textContent =
                                    profile.name.charAt(0).toUpperCase();
                                }}
                              />
                            ) : (
                              profile.name.charAt(0).toUpperCase()
                            )}
                          </div>
                        )}

                        {isEditMode && (
                          <button
                            className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-1 transform translate-x-2 -translate-y-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteProfile(profile.id);
                            }}
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                d="M6 18L18 6M6 6l12 12"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                              />
                            </svg>
                          </button>
                        )}
                      </div>

                      <span className="profile-name text-white">
                        {profile.name}
                      </span>
                    </button>
                  </div>
                );
              })}
            </AnimatePresence>
          </div>

          {profiles.length < 5 && (
            <button
              className="profile-add flex items-center"
              type="button"
              onClick={() => setShowProfileCreation(true)}
            >
              <i className="fa-solid fa-plus" />
              Add Profile
            </button>
          )}
        </div>

        {isEditMode && (
          <motion.button
            animate={{ opacity: 1 }}
            className="mt-8 mx-auto block px-6 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-full transition-colors z-10"
            initial={{ opacity: 0 }}
            onClick={() => setIsEditMode(false)}
          >
            Done Editing
          </motion.button>
        )}
      </motion.div>

      {/* Audio element for swoosh sound */}
      <audio preload="auto" ref={audioRef} src={swooshSound} />

      {/* Profile Menu Overlay */}
      <AnimatePresence>
        {showProfileMenu && selectedProfileForMenu && (
          <motion.div
            animate={{ opacity: 1 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 backdrop-blur-md"
            exit={{ opacity: 0 }}
            initial={{ opacity: 0 }}
            onClick={() => {
              setShowProfileMenu(false);
              setLogoutConfirm(false);
            }}
          >
            <motion.div
              animate={{ scale: 1, opacity: 1 }}
              className="w-[min(520px,38vw)] rounded-[28px] border border-white/15 px-8 pb-6 pt-7 shadow-2xl backdrop-blur-xl"
              exit={{ scale: 0.96, opacity: 0 }}
              initial={{ scale: 0.96, opacity: 0 }}
              style={{
                background:
                  "linear-gradient(180deg, rgba(28,28,40,0.72), rgba(20,20,28,0.78))",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {logoutConfirm ? (
                <div className="text-center text-white">
                  <h2 className="text-xl font-medium">Log Out?</h2>
                  <p className="mt-3 text-sm leading-relaxed text-white/80">
                    <span className="font-bold text-[#FF6B6B]">Warning:</span>{" "}
                    You will be signed out and need to sign in again to use
                    Lunova.
                  </p>
                  <div className="mt-6 flex justify-center gap-3">
                    <button
                      className="rounded-full border border-white/30 px-6 py-2 text-sm"
                      type="button"
                      onClick={() => setLogoutConfirm(false)}
                    >
                      Cancel
                    </button>
                    <button
                      className="rounded-full bg-[#FF4444] px-6 py-2 text-sm font-medium text-white"
                      type="button"
                      onClick={handleLogout}
                    >
                      Log Out
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <p className="text-center text-[10px] font-medium tracking-[0.24em] text-white/60">
                    MANAGE PROFILE
                  </p>
                  <div className="mx-auto mt-3 h-[84px] w-[84px] overflow-hidden rounded-full bg-gray-700">
                    {selectedProfileForMenu.avatar ? (
                      <img
                        alt=""
                        className="h-full w-full object-cover"
                        src={selectedProfileForMenu.avatar}
                      />
                    ) : (
                      <div className="flex h-full items-center justify-center text-2xl font-medium text-white">
                        {selectedProfileForMenu.name.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <p className="mt-2 text-center text-base font-medium text-white">
                    {selectedProfileForMenu.name}
                  </p>
                  <div className="mt-5 flex gap-6">
                    <button
                      className={`flex flex-1 flex-col items-center rounded-[22px] px-4 py-3 ${
                        menuFocus === "edit"
                          ? "bg-white text-[#0A0F1E]"
                          : "border border-white/30 bg-transparent text-[#F7F7FA]"
                      }`}
                      type="button"
                      onClick={() => {
                        setShowProfileMenu(false);
                        navigate(
                          `/web-player/edit-profile/${selectedProfileForMenu.id}`
                        );
                      }}
                      onMouseEnter={() => setMenuFocus("edit")}
                    >
                      <span className="flex items-center gap-2 text-xs font-medium">
                        <i className="fa-solid fa-pen-to-square text-base" />
                        Edit
                      </span>
                      <span
                        className={`mt-1.5 text-[10px] ${menuFocus === "edit" ? "text-[#0A0F1E]/60" : "text-white/50"}`}
                      >
                        Name & image
                      </span>
                    </button>
                    <button
                      className={`flex flex-1 flex-col items-center rounded-[22px] px-4 py-3 ${
                        menuFocus === "delete"
                          ? "bg-[#FF4444] text-white"
                          : "border border-white/30 bg-transparent text-[#F7F7FA]"
                      }`}
                      type="button"
                      onClick={() => {
                        handleDeleteProfile(selectedProfileForMenu.id);
                      }}
                      onMouseEnter={() => setMenuFocus("delete")}
                    >
                      <span className="flex items-center gap-2 text-xs font-medium">
                        <i className="fa-solid fa-trash-can text-base" />
                        Delete
                      </span>
                      <span
                        className={`mt-1.5 text-[10px] ${menuFocus === "delete" ? "text-white/80" : "text-white/50"}`}
                      >
                        Remove permanently
                      </span>
                    </button>
                  </div>
                  <div className="mx-0.5 my-4 h-px bg-white/10" />
                  <button
                    className={`flex w-full items-center justify-center gap-2 rounded-[22px] py-3 text-xs font-medium ${
                      menuFocus === "logout"
                        ? "bg-white text-[#0A0F1E]"
                        : "border border-white/30 bg-transparent text-[#F7F7FA]"
                    }`}
                    type="button"
                    onClick={() => setLogoutConfirm(true)}
                    onMouseEnter={() => setMenuFocus("logout")}
                  >
                    <i className="fa-solid fa-right-from-bracket" />
                    Logout of Lunova
                  </button>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showProfileCreation && (
          <motion.div
            animate={{ opacity: 1 }}
            className="profile-setup fixed inset-0 z-[60] flex items-center justify-center overflow-hidden"
            exit={{ opacity: 0 }}
            initial={{ opacity: 0 }}
          >
            <style>{`
              .profile-setup { --u: min(2px, calc(100vw / 960)); }
              .profile-setup-title {
                font-size: calc(24 * var(--u));
                font-weight: 700;
                color: #fff;
              }
              .profile-setup-subtitle {
                margin-top: calc(8 * var(--u));
                font-size: calc(16 * var(--u));
                color: #b0b0b0;
              }
              .profile-setup-row {
                display: flex;
                align-items: center;
                margin-top: calc(48 * var(--u));
              }
              .profile-setup-form { width: calc(300 * var(--u)); }
              .profile-setup-label {
                display: block;
                margin-bottom: calc(16 * var(--u));
                font-size: calc(12 * var(--u));
                color: #fff;
              }
              .profile-setup-input {
                width: 100%;
                height: calc(45 * var(--u));
                border-radius: calc(8 * var(--u));
                border: 1px solid #3d3d5c;
                background: #2d2d44;
                padding: 0 calc(20 * var(--u));
                font-size: calc(16 * var(--u));
                font-weight: 300;
                color: #fff;
                caret-color: #7dd3fc;
                outline: none;
              }
              .profile-setup-input::placeholder { color: #808080; }
              .profile-setup-copy {
                margin-top: calc(24 * var(--u));
                font-size: calc(10 * var(--u));
                font-weight: 300;
                line-height: 1.6;
                color: #808080;
              }
              .profile-setup-continue {
                display: flex;
                width: 100%;
                align-items: center;
                justify-content: center;
                gap: calc(10 * var(--u));
                margin-top: calc(24 * var(--u));
                border-radius: 999px;
                border: 1px solid rgba(255, 255, 255, 0.4);
                background: transparent;
                padding: calc(14 * var(--u)) calc(32 * var(--u));
                font-size: calc(16 * var(--u));
                font-weight: 500;
                color: #fff;
              }
              .profile-setup-continue:hover:not(:disabled) {
                background: #fff;
                color: #0a0f1e;
              }
              .profile-setup-continue:disabled { opacity: 1; color: #fff; }
              .profile-setup-avatar {
                position: relative;
                width: calc(154 * var(--u));
                height: calc(154 * var(--u));
                top: -10vh;
                margin-left: calc(48 * var(--u));
                flex: 0 0 auto;
                border-radius: 999px;
                background: #212433;
              }
              .profile-setup-avatar img {
                width: 100%;
                height: 100%;
                border-radius: 999px;
                object-fit: cover;
                background: #212433;
              }
              .profile-setup-edit {
                position: absolute;
                right: calc(4 * var(--u));
                bottom: calc(4 * var(--u));
                display: flex;
                width: calc(32 * var(--u));
                height: calc(32 * var(--u));
                align-items: center;
                justify-content: center;
                border-radius: 999px;
                background: #fff;
                color: #1a1a1a;
                font-size: calc(14 * var(--u));
              }
              .profile-setup-content {
                padding-bottom: calc(80 * var(--u));
              }
              .profile-setup-logo {
                position: absolute;
                left: calc(20 * var(--u));
                bottom: calc(72 * var(--u));
                width: auto;
                height: auto;
                max-width: 14%;
                max-height: calc(36 * var(--u));
                object-fit: contain;
                object-position: left bottom;
              }
            `}</style>
            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(to top right, #030305 0%, #0E0E18 50%, #1C1C2A 100%)",
              }}
            >
              {backdrop ? (
                <img
                  alt=""
                  className="h-full w-full object-cover"
                  src={backdrop}
                  style={{ opacity: 0.2 }}
                />
              ) : null}
              {backdrop ? (
                <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-black/20 to-transparent" />
              ) : null}
            </div>
            {movieLogo ? (
              <img alt="" className="profile-setup-logo z-10" src={movieLogo} />
            ) : null}
            <div className="profile-setup-content relative z-10 flex flex-col items-center">
              <h1 className="profile-setup-title">
                Let&apos;s set up your profile
              </h1>
              <p className="profile-setup-subtitle">
                Help us give you a more personalized experience.
              </p>
              <div className="profile-setup-row">
                <div className="profile-setup-form">
                  <label className="profile-setup-label">PROFILE NAME</label>
                  <input
                    autoFocus
                    className="profile-setup-input"
                    placeholder="Enter profile name"
                    type="text"
                    value={newProfileName}
                    onChange={(e) => setNewProfileName(e.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") handleCreateProfile();
                    }}
                  />
                  <p className="profile-setup-copy">
                    We use profiles to give you and the rest of your household a
                    personalized experience. That means everyone gets their own
                    space, and no one has to suffer through your cousin&apos;s
                    weird documentary phase.
                  </p>
                  <button
                    className="profile-setup-continue"
                    disabled={!newProfileName.trim() || creatingProfile}
                    type="button"
                    onClick={handleCreateProfile}
                  >
                    <i className="fa-solid fa-plus" />
                    {creatingProfile ? "Creating..." : "Continue"}
                  </button>
                  {!isFirstTimeUser && (
                    <button
                      className="mx-auto mt-4 block text-sm text-white/50 hover:text-white"
                      type="button"
                      onClick={() => {
                        setShowProfileCreation(false);
                        setNewProfileName("");
                        setSelectedAvatar(null);
                      }}
                    >
                      Back to Profiles
                    </button>
                  )}
                </div>
                <button
                  className="profile-setup-avatar"
                  type="button"
                  onClick={() => setShowAvatarModal(true)}
                >
                  <img
                    alt="Profile avatar"
                    src={
                      selectedAvatar
                        ? typeof selectedAvatar === "string"
                          ? selectedAvatar
                          : selectedAvatar.url || selectedAvatar.image
                        : lunovaLogo
                    }
                  />
                  <span className="profile-setup-edit">
                    <i className="fa-solid fa-pencil" />
                  </span>
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Avatar Selection Modal - Full Page */}
      <AnimatePresence>
        {showAvatarModal && (
          <motion.div
            animate={{ opacity: 1 }}
            className="fixed inset-0 z-[80] flex items-center justify-center"
            exit={{ opacity: 0 }}
            initial={{ opacity: 0 }}
            style={{
              background:
                "linear-gradient(to top right, #030305 0%, #0E0E18 50%, #1C1C2A 100%)",
            }}
          >
            <motion.div
              animate={{ opacity: 1, y: 0 }}
              className="w-full h-full flex flex-col p-4 sm:p-6 md:p-8"
              exit={{ opacity: 0, y: 20 }}
              initial={{ opacity: 0, y: 20 }}
            >
              {/* Fixed Header */}
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-light text-white mb-6 sm:mb-8 md:mb-12 text-center">
                Choose Your Avatar
              </h2>

              {/* Scrollable Content Area */}
              <div className="flex-1 overflow-y-auto px-2 sm:px-4">
                {loadingAvatars ? (
                  <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500" />
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 sm:gap-6 md:gap-8 lg:gap-10 max-w-7xl mx-auto pb-4">
                    {availableAvatars.map((avatar, index) => {
                      const isStringAvatar = typeof avatar === "string";
                      const avatarUrl = isStringAvatar
                        ? avatar
                        : avatar.url || avatar.image;
                      const avatarKey = isStringAvatar
                        ? avatar
                        : avatar.id || index;

                      return (
                        <button
                          className="group relative cursor-pointer transition-transform duration-200 hover:scale-110 mx-auto"
                          key={avatarKey}
                          onClick={() => {
                            setSelectedAvatar(isStringAvatar ? avatar : avatar);
                            setShowAvatarModal(false);
                            toast.success("Avatar selected!");
                            // When selecting avatar in create profile mode, just close modal
                            // The avatar is already set, user will click Continue
                          }}
                        >
                          <div
                            className={`w-32 h-32 sm:w-36 sm:h-36 md:w-40 md:h-40 rounded-full overflow-hidden border-4 transition-colors duration-200 ${
                              (
                                isStringAvatar
                                  ? selectedAvatar === avatar
                                  : selectedAvatar?.id === avatar.id
                              )
                                ? "border-blue-400"
                                : "border-transparent group-hover:border-blue-400"
                            }`}
                          >
                            {avatarUrl ? (
                              <img
                                alt={`Avatar ${index + 1}`}
                                className="w-full h-full object-cover"
                                src={avatarUrl}
                                onError={(e) => {
                                  e.target.style.display = "none";
                                  e.target.parentElement.innerHTML = `<div class="w-full h-full bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center text-white text-2xl font-bold">${
                                    index + 1
                                  }</div>`;
                                }}
                              />
                            ) : (
                              <div
                                className="w-full h-full flex items-center justify-center text-white text-xl sm:text-2xl font-bold"
                                style={{
                                  backgroundColor:
                                    avatar.bg || `hsl(${index * 40}, 70%, 50%)`,
                                }}
                              >
                                {avatar.initial ||
                                  avatar.name?.charAt(0) ||
                                  String.fromCharCode(65 + index)}
                              </div>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Fixed Bottom Button */}
              <div className="mt-6 sm:mt-8 flex justify-center">
                <button
                  className="px-8 sm:px-12 py-3 sm:py-4 bg-gray-800/70 hover:bg-gray-700/70 text-white text-base sm:text-lg rounded-full transition-colors min-w-[150px] sm:min-w-[200px]"
                  onClick={() => setShowAvatarModal(false)}
                >
                  Back to Profile Setup
                </button>
              </div>
            </motion.div>

            {/* Dashboard Button */}
            <button
              className="fixed bottom-4 right-4 sm:bottom-8 sm:right-8 z-20 text-white text-xs sm:text-sm font-medium bg-black/50 px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg opacity-40 hover:opacity-100 hover:scale-105 transition-all duration-300"
              onClick={() =>
                window.open("https://dashboard.lunova.app", "_blank")
              }
            >
              Dashboard
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default WebPlayerProfiles;
